import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { DownloadIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Button,
	FormControl,
	HStack,
	Icon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	SimpleGrid,
	Td,
	Text,
	Textarea,
	Tr,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCheckCircle, FiClock, FiFileText, FiImage, FiInfo, FiMapPin, FiMessageSquare, FiUser, FiXCircle } from "react-icons/fi";

import {
	MODAL_GRID_SPACING,
	MODAL_SECTION_SPACING,
	PropertyConfirmModal,
	PropertyModalSection,
	PropertyModalShell,
	ModalFieldLabel,
	usePropertyModalTheme,
} from "../PropertyModal/shared";
import { addReplyToTicketAsync, markTicketAsDoneAsync, removeTicketAsync } from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import { API } from "../../utils/http";
import { fullDateTimeFormat } from "../../utils/types";
import Dropzone from "../Dropzone/Dropzone";
import Gallery from "../Gallery/Gallery";

function TicketsTableRow(props) {
	const { row, author, owner } = props;
	const { t } = useTranslation();
	const newStatusBackground = "red";
	const pendingStatusBackground = "yellow";
	const resolvedStatusBackground = "green";
	const { isOpen: isDeleteConfirmationOpen, onOpen: onDeleteConfirmationOpen, onClose: onDeleteConfirmationClose } = useDisclosure();

	const { isOpen, onOpen, onClose } = useDisclosure();
	const dispatch = useDispatch();
	const initialReply = { message: "" };
	const [documents, setDocuments] = useState([]);
	const [media, setMedia] = useState([]);
	const [reply, setReply] = useState(initialReply);
	const [images, setImages] = useState([]);
	const toast = useToast();
	const { borderColor, iconColor } = usePropertyModalTheme();
	const descriptionBg = useColorModeValue("gray.50", "gray.700");

	const handleChange = (e) => {
		setReply({ message: e.target.value });
	};
	const handleGetDocument = (documentId, documentName) => {
		const payload = { propertyId: row.propertyId, documentId, ticketId: row.id, documentName };
		console.log(payload);
		API.put("get-ticket-document", payload)
			.then((r) => {
				console.log(r);
				window.open(r.data.url, "_blank");
			})
			.catch((e) => {
				console.log(e);
				toast({
					title: "Error encountered",
					description: "Error encountered while fetching document",
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			});
	};
	const saveTicket = (e) => {
		const payload = { author, reply, ticketId: row.id, propertyId: row.propertyId, documents, media };
		console.log(payload);
		dispatch(addReplyToTicketAsync(payload)).finally(() => {
			onClose(e);
			setDocuments([]);
			setMedia([]);
			setReply(initialReply);
		});
	};
	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const documentList = [];
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			documentList.push({ documentData: _arrayBufferToBase64(buffer), documentName, contentType });
		}

		setDocuments(documentList);
	};
	const onMediaDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		const mediaList = [];
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			mediaList.push({ documentData: _arrayBufferToBase64(buffer), documentName, contentType });
		}

		setMedia(mediaList);
	};
	useEffect(() => {
		const payload = { propertyId: row.propertyId, media: row.media, ticketId: row.id };
		API.put("get-ticket-media-urls", payload).then((r) => {
			setImages(r.data.media);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [row.media]);
	const markTicketAsDone = () => {
		const payload = { author, propertyId: row.propertyId, ticketId: row.id };
		dispatch(markTicketAsDoneAsync(payload)).then(() => {
			onClose();
		});
	};
	const handleDeleteTicket = () => {
		const payload = { propertyId: row.propertyId, ticketId: row.id };
		dispatch(removeTicketAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Ticket deleted!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.catch((e) => {
				console.error(e);
				toast({
					title: "Error encountered",
					description: "Error encountered while deleting ticket",
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			})
			.finally(() => {
				onDeleteConfirmationClose();
			});
	};

	const statusBadge = (
		<Badge
			colorScheme={row.status === "done" ? resolvedStatusBackground : row.status === "pending" ? pendingStatusBackground : newStatusBackground}
			fontSize="xs"
			px={2}
			py={0.5}
			borderRadius="full"
		>
			{t(`types.${row.status}`)}
		</Badge>
	);

	const renderDeleteConfirmationModal = () => (
		<PropertyConfirmModal
			isOpen={isDeleteConfirmationOpen}
			onClose={onDeleteConfirmationClose}
			onConfirm={handleDeleteTicket}
			message={t("property.deleteTicketConfirmation")}
			emphasis={t("property.actionCannotBeUndone")}
		/>
	);

	const renderTicketModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="5xl"
			icon={FiMessageSquare}
			title={t("property.ticketDetailsTitle")}
			subtitle={t("property.viewTicketSubtitle")}
			headerExtra={statusBadge}
			footer={
				<HStack spacing={3} w="full" justify="space-between" borderTop="1px" borderColor={borderColor} pt={3} pb={4} px={6}>
					<Button size="sm" colorScheme="green" leftIcon={<Icon as={FiCheckCircle} />} onClick={markTicketAsDone} isDisabled={row.status === "done"}>
						{t("property.markAsDone")}
					</Button>
					<HStack spacing={3}>
						<Button size="sm" leftIcon={<Icon as={FiXCircle} />} variant="outline" onClick={onClose}>
							{t("misc.cancel")}
						</Button>
						<Button
							size="sm"
							colorScheme="blue"
							leftIcon={<Icon as={FiMessageSquare} />}
							onClick={saveTicket}
							isDisabled={row.status === "done"}
							px={6}
						>
							{t("misc.save")}
						</Button>
					</HStack>
				</HStack>
			}
		>
			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={MODAL_GRID_SPACING}>
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiInfo} title={t("property.ticketSummary")}>
						<VStack spacing={3} align="stretch">
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiMessageSquare} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.title")}
									</Text>
								</HStack>
								<Text fontSize="sm" fontWeight="bold">
									{row.title}
								</Text>
							</Box>
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiUser} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.author")}
									</Text>
								</HStack>
								<Text fontSize="sm">{row.author.name}</Text>
							</Box>
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiMapPin} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.address")}
									</Text>
								</HStack>
								<Text fontSize="sm">{row?.address}</Text>
							</Box>
							<Box>
								<HStack spacing={2} mb={1}>
									<Icon as={FiClock} color={iconColor} boxSize={4} />
									<Text fontSize="sm" fontWeight="medium">
										{t("table.addedDate")}
									</Text>
								</HStack>
								<Text fontSize="sm">{moment(row.addedDate).format(fullDateTimeFormat)}</Text>
							</Box>
						</VStack>
					</PropertyModalSection>

					<PropertyModalSection icon={FiFileText} title={t("table.description")}>
						<Text fontSize="sm" bg={descriptionBg} p={3} borderRadius="md">
							{row.description}
						</Text>
					</PropertyModalSection>

					{images && images.length > 0 && (
						<PropertyModalSection
							icon={FiImage}
							title={t("property.mediaGallery")}
							headerExtra={
								<Badge colorScheme="blue" fontSize="xs" px={2} py={0.5} borderRadius="full">
									{images.length}
								</Badge>
							}
						>
							<Gallery items={images} />
						</PropertyModalSection>
					)}
				</VStack>

				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					{row.documents && row.documents.length > 0 && (
						<PropertyModalSection
							icon={FiFileText}
							title={t("property.documentsLabel")}
							headerExtra={
								<Badge colorScheme="blue" fontSize="xs" px={2} py={0.5} borderRadius="full">
									{row.documents.length}
								</Badge>
							}
						>
							<VStack spacing={2} align="stretch">
								{row.documents.map((d, index) => (
									<Box
										key={index}
										p={3}
										border="1px"
										borderColor={borderColor}
										borderRadius="md"
										_hover={{ bg: descriptionBg }}
										cursor="pointer"
										onClick={() => handleGetDocument(d.id, d.name)}
									>
										<HStack justify="space-between">
											<HStack spacing={3}>
												<Icon as={FiFileText} color={iconColor} boxSize={4} />
												<VStack align="start" spacing={0}>
													<Text fontSize="sm" fontWeight="medium" noOfLines={1}>
														{d.name}
													</Text>
													<Text fontSize="xs" color="gray.500">
														{t("property.clickToDownload")}
													</Text>
												</VStack>
											</HStack>
											<Icon as={DownloadIcon} color={iconColor} boxSize={4} />
										</HStack>
									</Box>
								))}
							</VStack>
						</PropertyModalSection>
					)}

					<PropertyModalSection
						icon={FiMessageSquare}
						title={t("property.ticketConversation")}
						headerExtra={
							<Badge colorScheme="green" fontSize="xs" px={2} py={0.5} borderRadius="full">
								{row.replies.length}
							</Badge>
						}
					>
						<VStack spacing={3} align="stretch">
							{row.replies.length > 0 ? (
								<VStack spacing={3} align="stretch">
									{row.replies.map((m, index) => (
										<Box key={index} p={3} border="1px" borderColor={borderColor} borderRadius="md" bg={descriptionBg}>
											<HStack spacing={3} mb={2}>
												<Icon as={FiUser} color={iconColor} boxSize={4} />
												<Text fontSize="sm" fontWeight="bold">
													{m.author.name}
												</Text>
												<Text fontSize="xs" color="gray.500">
													{moment(m.timestamp).format("DD MMM YYYY - HH:mm")}
												</Text>
											</HStack>
											<Text fontSize="sm" pl={7}>
												{m.message}
											</Text>
										</Box>
									))}
								</VStack>
							) : (
								<Box textAlign="center" py={4}>
									<Icon as={FiMessageSquare} color="gray.300" boxSize={6} mb={2} />
									<Text fontSize="sm" color="gray.500">
										{t("property.noRepliesYet")}
									</Text>
								</Box>
							)}

							{row.status !== "done" && (
								<Box>
									<FormControl>
										<ModalFieldLabel icon={FiMessageSquare}>{t("property.yourResponse")}</ModalFieldLabel>
										<Textarea
											size="sm"
											name="message"
											placeholder={t("property.typeYourMessage")}
											value={reply.message}
											onChange={handleChange}
											borderRadius="md"
											rows={3}
										/>
									</FormControl>

									<SimpleGrid columns={2} spacing={4} mt={3}>
										<FormControl>
											<ModalFieldLabel icon={FiImage}>{t("property.mediaFiles")}</ModalFieldLabel>
											<Dropzone onDrop={onMediaDrop} accept={{ "image/*": [], "video/*": [] }} description={t("property.uploadImagesOrVideos")} />
										</FormControl>

										<FormControl>
											<ModalFieldLabel icon={FiFileText}>{t("property.documentsLabel")}</ModalFieldLabel>
											<Dropzone
												onDrop={onDocumentDrop}
												accept={{
													"application/pdf": [".pdf"],
													"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
													"application/msword": [".doc"],
												}}
												description={t("property.uploadDocumentsDescription")}
											/>
										</FormControl>
									</SimpleGrid>
								</Box>
							)}
						</VStack>
					</PropertyModalSection>
				</VStack>
			</SimpleGrid>
		</PropertyModalShell>
	);

	return (
		<Tr>
			<Td>
				<Text>{moment(row.addedDate).format(fullDateTimeFormat)}</Text>
			</Td>
			<Td>
				<Text>{owner.name}</Text>
			</Td>
			<Td>
				<Text>{row.address}</Text>
			</Td>
			<Td>
				<Badge colorScheme={row.status === "done" ? resolvedStatusBackground : row.status === "pending" ? pendingStatusBackground : newStatusBackground}>
					{t(`types.${row.status}`)}
				</Badge>
			</Td>
			<Td>
				<Text>{row.title}</Text>
			</Td>
			<Td>
				<Text>{row.author.name}</Text>
			</Td>
			<Td>
				<Menu closeOnSelect={false}>
					<MenuButton>
						<Icon as={HamburgerIcon} />
					</MenuButton>
					<MenuList>
						<MenuItem onClick={onOpen}>View</MenuItem>
						<MenuItem onClick={onDeleteConfirmationOpen}>Delete</MenuItem>
					</MenuList>
				</Menu>
				{renderTicketModal()}
				{renderDeleteConfirmationModal()}
			</Td>
		</Tr>
	);
}

export default TicketsTableRow;
